/**
 * Company Follow API Service
 *
 * Дагадаг (candidate follows a company, explicit — the follow button on the company
 * page) and Дагагчид (companies that follow the candidate back, automatic — recorded
 * server-side as a side effect of a company viewing the candidate's public profile,
 * see getPublicProfile in candidate.js — there is no toggle for that direction here).
 *
 * Same response shape as assessmentFavorite.js, same reasoning for it.
 */

import http from '../client';

const handleApiSuccess = (response) => ({
  success: true,
  data: response.data?.data || response.data,
  status: response.status,
  message: response.data?.message || 'Success'
});

const handleApiError = (error) => ({
  success: false,
  error: error.message || 'An error occurred',
  status: error.status || 500,
  requiresAuth: error.status === 401
});

/**
 * Toggle following a company.
 * @param {number} companyId
 * @returns {Promise<Object>} { success, data: { isFollowing, companyId } }
 */
export const toggleCompanyFollow = async (companyId) => {
  try {
    const response = await http.post('/diil/company-follow/toggle', null, {
      params: { companyId }
    });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Whether the current candidate already follows this company.
 * @param {number} companyId
 * @returns {Promise<Object>} { success, data: { isFollowing, companyId } }
 */
export const checkCompanyFollow = async (companyId) => {
  try {
    const response = await http.get('/diil/company-follow/check', { companyId });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * How many candidates follow this company — shown on the company's own page.
 * @param {number} companyId
 * @returns {Promise<Object>} { success, data: { count } }
 */
export const getCompanyFollowerCount = async (companyId) => {
  try {
    const response = await http.get('/diil/company-follow/count', { companyId });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * The candidates following a company — shown on the company's own page to its own
 * people. The server refuses this for anyone else (the count is public, the list is not).
 * @param {number} companyId
 * @returns {Promise<Object>} { success, data: Array<{candidateId, candidateName, candidatePhoto}> }
 */
export const getCompanyFollowers = async (companyId) => {
  try {
    const response = await http.get('/diil/company-follow/followers', { companyId });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Companies the current candidate follows (Дагадаг tab).
 * @returns {Promise<Object>} { success, data: Array<{companyId, companyName, companyPhoto}> }
 */
export const getFollowedCompanies = async () => {
  try {
    const response = await http.get('/diil/candidates/me/following');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * A "Профайл үзсэн" row as ProfileListSheet renders it.
 *
 * The list has two kinds of viewer — a company that opened the profile, and a person who
 * did — and the server tags each row with `kind`. A company row already carries the
 * companyId/companyName/companyPhoto shape the sheet reads by default; a person row has to
 * say where it opens, since the sheet's default destination is /company/{id}.
 */
const toViewerRow = (row) => {
  if (row?.kind !== 'person') return row;
  return {
    id: row.viewerCandidateId,
    name: row.name,
    photo: row.photo,
    to: `/candidate/${row.viewerCandidateId}`,
    // Drives the sheet's fallback icon: a person outline rather than a building.
    kind: 'person',
    createdDate: row.createdDate,
  };
};

/**
 * Everyone who has opened the current candidate's profile — companies and people alike
 * (the "Профайл үзсэн" list).
 * @returns {Promise<Object>} { success, data: Array<row> } — see toViewerRow
 */
export const getFollowers = async () => {
  try {
    const response = await http.get('/diil/candidates/me/followers');
    const result = handleApiSuccess(response);
    return Array.isArray(result.data)
      ? { ...result, data: result.data.map(toViewerRow) }
      : result;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * How many companies the current candidate follows — for the Дагадаг count, without
 * fetching the full list.
 * @returns {Promise<Object>} { success, data: { count } }
 */
export const getFollowedCompaniesCount = async () => {
  try {
    const response = await http.get('/diil/candidates/me/following/count');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * How many companies follow the current candidate — for the Дагагчид count, without
 * fetching the full list.
 * @returns {Promise<Object>} { success, data: { count } }
 */
export const getMyFollowersCount = async () => {
  try {
    const response = await http.get('/diil/candidates/me/followers/count');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Companies a given candidate follows — for viewing someone else's Дагадаг on their
 * public profile (src/page/CandidateProfile.jsx), not just "me".
 * @param {number} candidateId
 * @returns {Promise<Object>} { success, data: Array<{companyId, companyName, companyPhoto}> }
 */
export const getFollowedCompaniesFor = async (candidateId) => {
  try {
    const response = await http.get(`/diil/candidates/${candidateId}/following`);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Who has opened a given candidate's profile — for the "Профайл үзсэн" list on someone
 * else's public profile.
 *
 * <b>Companies only.</b> Who a stranger's profile was read by is that person's business,
 * not a visitor's: which people looked at them says something about them that they did not
 * publish, and on their own profile (getFollowers above) they see all of it. So the person
 * rows are dropped here — the company rows stay, because a company opening a profile is an
 * employer taking an interest, which is the very thing the list was built to show.
 *
 * Dropped rather than asked-for-differently on purpose: the server answers this route the
 * same way for everybody, so the filter has to hold whatever it sends. It is not a
 * substitute for the server doing the same — a determined reader can still call the
 * endpoint — which is why the row itself carries no phone, email or anything else private.
 *
 * @param {number} candidateId
 * @returns {Promise<Object>} { success, data: Array<row> } — see toViewerRow
 */
export const getFollowersFor = async (candidateId) => {
  try {
    const response = await http.get(`/diil/candidates/${candidateId}/followers`);
    const result = handleApiSuccess(response);
    return Array.isArray(result.data)
      ? { ...result, data: result.data.filter((row) => row?.kind !== 'person').map(toViewerRow) }
      : result;
  } catch (error) {
    return handleApiError(error);
  }
};

const companyFollowService = {
  toggleCompanyFollow,
  checkCompanyFollow,
  getCompanyFollowerCount,
  getCompanyFollowers,
  getFollowedCompanies,
  getFollowers,
  getFollowedCompaniesCount,
  getMyFollowersCount,
  getFollowedCompaniesFor,
  getFollowersFor
};

export default companyFollowService;
